import { FC, useEffect, useRef, useState } from 'react';
import { MapPin, Check, X, Search, HelpCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { useLanguage } from '@/contexts/LanguageContext';
import OpenLocationCode from 'open-location-code';
import { serviceColors, locationColors, statusColors, getPrecisionLevel, getTextHex } from '@/lib/colors';

interface LocationPickerProps {
  isOpen: boolean;
  onClose: () => void;
  onLocationSelect: (coords: { lat: number; lng: number }) => void;
  initialLocation?: { lat: number; lng: number };
  currentUserLocation?: { lat: number; lng: number };
}

// Declare Leaflet globally
declare global {
  interface Window {
    L: any;
  }
}

export const LocationPicker: FC<LocationPickerProps> = ({
  isOpen,
  onClose,
  onLocationSelect,
  initialLocation,
  currentUserLocation
}) => {
  const { theme } = useTheme();
  const { t } = useLanguage();
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const tileLayerRef = useRef<any>(null);
  const selectedMarkerRef = useRef<any>(null);
  const currentLocationMarkerRef = useRef<any>(null);
  const accuracyCircleRef = useRef<any>(null);
  
  const [isMapReady, setIsMapReady] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [mapError, setMapError] = useState<string | null>(null);
  const [selectedCoords, setSelectedCoords] = useState<{ lat: number; lng: number } | null>(
    initialLocation || null
  );
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number; accuracy?: number } | null>(
    null // Don't initialize from props - let GPS detection handle it
  );
  const [coordinateInput, setCoordinateInput] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [showHelp, setShowHelp] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState<string | null>(null);
  const [isLoadingAddress, setIsLoadingAddress] = useState(false);

  // Default center (Indonesia)
  const defaultCenter = { lat: -2.5489, lng: 118.0149 };

  // Helper function to get tile configuration
  // ✅ TEMPORARY: Using standard OpenStreetMap theme for all modes
  const getTileConfig = (_currentTheme: 'light' | 'dark') => {
    // Always use standard OSM tiles regardless of theme
    return {
      url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      subdomains: 'abc',
      maxZoom: 19
    };
  };

  // Get optimal zoom level based on GPS accuracy
  const getAccuracyBasedZoom = (accuracy?: number): number => {
    if (!accuracy) {
      return 7; // No GPS accuracy data - country level view
    }
    
    if (accuracy <= 50) {
      return 17; // High precision (≤50m) - building level
    } else if (accuracy <= 200) {
      return 11; // Medium precision (~50-200m) - neighborhood level  
    } else {
      return 9; // Low precision (>200m) - city level
    }
  };

  // Get zoom level for location context
  const getContextZoom = (location?: { lat: number; lng: number; accuracy?: number }, hasGPS: boolean = false): number => {
    if (location?.accuracy) {
      return getAccuracyBasedZoom(location.accuracy);
    } else if (hasGPS && location) {
      return 15; // GPS location without accuracy data - street level
    } else if (location) {
      return 10; // Manual/saved location - city level (better for range examination)
    } else {
      return 7; // No location data - country level view for manual selection
    }
  };

  // Load Leaflet when modal opens
  useEffect(() => {
    if (!isOpen) return;

    const loadLeaflet = async () => {
      try {
        setIsLoading(true);
        
        if (window.L) {
          setIsMapReady(true);
          setIsLoading(false);
          return;
        }

        // Load Leaflet CSS
        const cssLink = document.createElement('link');
        cssLink.rel = 'stylesheet';
        cssLink.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
        cssLink.integrity = 'sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY=';
        cssLink.crossOrigin = '';
        document.head.appendChild(cssLink);

        // Load Leaflet JS
        const script = document.createElement('script');
        script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
        script.integrity = 'sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo=';
        script.crossOrigin = '';
        
        script.onload = () => {
          console.log('🗺️ Leaflet loaded for LocationPicker');
          setIsMapReady(true);
          setIsLoading(false);
        };
        
        script.onerror = () => {
          console.error('🗺️ Failed to load Leaflet for LocationPicker');
          setMapError('Failed to load map library');
          setIsLoading(false);
        };
        
        document.head.appendChild(script);
      } catch (error) {
        console.error('🗺️ Error loading Leaflet:', error);
        setMapError('Error initializing map');
        setIsLoading(false);
      }
    };

    loadLeaflet();
  }, [isOpen]);

  // Initialize map
  useEffect(() => {
    if (!isOpen || !isMapReady || !mapRef.current || !window.L) return;

    try {
      console.log('🗺️ Initializing LocationPicker map...');
      
      // Clear existing instance
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }

      // Determine initial center and zoom level based on available locations
      const mapCenter = userLocation || currentUserLocation || initialLocation || defaultCenter;
      const initialZoom = getContextZoom(
        userLocation || currentUserLocation || initialLocation,
        !!(userLocation || currentUserLocation)
      );
      
      const map = window.L.map(mapRef.current).setView([mapCenter.lat, mapCenter.lng], initialZoom);
      
      // Add theme-appropriate tiles
      const tileConfig = getTileConfig(theme);
      const tileLayer = window.L.tileLayer(tileConfig.url, {
        attribution: tileConfig.attribution,
        subdomains: tileConfig.subdomains,
        maxZoom: tileConfig.maxZoom
      }).addTo(map);
      
      // Store tile layer reference for theme switching
      tileLayerRef.current = tileLayer;

      // Map click handler for location selection
      map.on('click', (e: any) => {
        const coords = { lat: e.latlng.lat, lng: e.latlng.lng };
        setSelectedCoords(coords);
        addSelectedLocationMarker(map, coords);
      });

      mapInstanceRef.current = map;

      // Add existing markers
      if (userLocation) {
        addCurrentLocationMarker(map, userLocation);
      }
      if (selectedCoords) {
        addSelectedLocationMarker(map, selectedCoords);
      }

      console.log('🗺️ LocationPicker map initialized');
    } catch (error) {
      console.error('🗺️ Error initializing LocationPicker map:', error);
      setMapError('Error creating map');
    }
  }, [isOpen, isMapReady, userLocation, currentUserLocation, initialLocation, theme]);

  // Handle theme changes for existing map
  useEffect(() => {
    if (!mapInstanceRef.current || !tileLayerRef.current || !window.L) return;

    // Remove existing tile layer
    mapInstanceRef.current.removeLayer(tileLayerRef.current);
    
    // Add new theme-appropriate tile layer
    const tileConfig = getTileConfig(theme);
    const newTileLayer = window.L.tileLayer(tileConfig.url, {
      attribution: tileConfig.attribution,
      subdomains: tileConfig.subdomains,
      maxZoom: tileConfig.maxZoom
    }).addTo(mapInstanceRef.current);
    
    // Update reference
    tileLayerRef.current = newTileLayer;
    
    console.log(`🗺️ LocationPicker theme: Using standard OpenStreetMap (ignoring ${theme} mode)`);
  }, [theme]);

  // Add current location marker
  const addCurrentLocationMarker = (map: any, location: { lat: number; lng: number; accuracy?: number }) => {
    if (!window.L || !map) return;

    // Remove existing
    if (currentLocationMarkerRef.current) {
      map.removeLayer(currentLocationMarkerRef.current);
    }
    if (accuracyCircleRef.current) {
      map.removeLayer(accuracyCircleRef.current);
    }

    // Add accuracy circle if available with precision-based styling
    if (location.accuracy && location.accuracy > 0) {
      const accuracyRadius = Math.min(location.accuracy, 1000);

      // Color and opacity based on accuracy level
      const circleColor = location.accuracy <= 50
        ? locationColors.high()
        : location.accuracy <= 200
          ? locationColors.medium()
          : locationColors.low();

      const fillOpacity = location.accuracy <= 50
        ? 0.15
        : location.accuracy <= 200
          ? 0.12
          : 0.08;
      
      accuracyCircleRef.current = window.L.circle([location.lat, location.lng], {
        radius: accuracyRadius,
        fillColor: circleColor,
        fillOpacity: fillOpacity,
        color: circleColor,
        weight: 2,
        opacity: 0.4
      }).addTo(map);
    }

    // Current location marker (Google blue)
    const googleBlue = serviceColors.google();
    const currentLocationIcon = window.L.divIcon({
      className: 'current-location-marker',
      html: `
        <div style="
          background-color: ${googleBlue};
          border: 3px solid white;
          border-radius: 50%;
          width: 20px;
          height: 20px;
          box-shadow: 0 2px 6px ${googleBlue}66;
          position: relative;
        ">
          <div style="
            position: absolute;
            top: -3px;
            left: -3px;
            width: 26px;
            height: 26px;
            border: 2px solid ${googleBlue};
            border-radius: 50%;
            opacity: 0.3;
            animation: ping 2s cubic-bezier(0, 0, 0.2, 1) infinite;
          "></div>
        </div>
        <style>
          @keyframes ping {
            75%, 100% {
              transform: scale(1.5);
              opacity: 0;
            }
          }
        </style>
      `,
      iconSize: [20, 20],
      iconAnchor: [10, 10]
    });

    const marker = window.L.marker([location.lat, location.lng], { icon: currentLocationIcon }).addTo(map);

    const precision = getPrecisionLevel(location.accuracy);
    const textSecondary = getTextHex('secondary');

    marker.bindPopup(`
      <div style="padding: 8px; min-width: 180px;">
        <div style="font-weight: bold; margin-bottom: 4px;">📍 Your Current Location</div>
        <div style="font-size: 11px; color: ${textSecondary}; margin-bottom: 2px;">
          ${location.lat.toFixed(6)}, ${location.lng.toFixed(6)}
        </div>
        ${location.accuracy ? `
          <div style="font-size: 11px; color: ${textSecondary}; margin-bottom: 2px;">
            Accuracy: ±${Math.round(location.accuracy)}m
          </div>
          <div style="font-size: 10px; color: ${precision.color}; font-weight: bold;">
            ${precision.level}
          </div>
        ` : ''}
      </div>
    `);

    currentLocationMarkerRef.current = marker;
  };

  // Add selected location marker
  const addSelectedLocationMarker = (map: any, coords: { lat: number; lng: number }) => {
    if (!window.L || !map) return;

    // Remove existing selected marker
    if (selectedMarkerRef.current) {
      map.removeLayer(selectedMarkerRef.current);
    }

    // Selected location marker (red crosshair without border)
    const errorRed = statusColors.error();
    const selectedLocationIcon = window.L.divIcon({
      className: 'selected-location-marker',
      html: `
        <div style="
          background-color: ${errorRed};
          border-radius: 50%;
          width: 20px;
          height: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 2px 8px ${errorRed}80, 0 0 0 3px ${errorRed}33;
          position: relative;
        ">
          <div style="
            color: white;
            font-size: 14px;
            font-weight: bold;
            line-height: 1;
          ">×</div>
        </div>
      `,
      iconSize: [20, 20],
      iconAnchor: [10, 10]
    });

    const marker = window.L.marker([coords.lat, coords.lng], { icon: selectedLocationIcon }).addTo(map);

    const textSecondary = getTextHex('secondary');

    marker.bindPopup(`
      <div style="padding: 8px; min-width: 180px;">
        <div style="font-weight: bold; margin-bottom: 4px;">🎯 Selected Location</div>
        <div style="font-size: 11px; color: ${textSecondary}; margin-bottom: 4px;">
          ${coords.lat.toFixed(6)}, ${coords.lng.toFixed(6)}
        </div>
        <div style="font-size: 10px; color: ${textSecondary};">
          Click elsewhere to change location
        </div>
      </div>
    `);

    selectedMarkerRef.current = marker;
  };



  // Parse Plus Code (Open Location Code)
  const parsePlusCode = (input: string): { lat: number; lng: number } | null => {
    try {
      const cleaned = input.trim().toUpperCase();

      // Check if it looks like a Plus Code (contains + symbol)
      if (!cleaned.includes('+')) {
        return null;
      }

      // Try to decode the Plus Code
      const olc = new OpenLocationCode();

      // Check if it's a valid Plus Code format
      if (!olc.isValid(cleaned)) {
        return null;
      }

      // Check if it's a short code (needs reference location)
      if (olc.isShort(cleaned)) {
        // For short codes, we need a reference location
        // Use user's current location if available, otherwise use default center
        const refLocation = userLocation || defaultCenter;

        // Recover the full code using reference location
        const fullCode = olc.recoverNearest(cleaned, refLocation.lat, refLocation.lng);
        const decoded = olc.decode(fullCode);

        return {
          lat: decoded.latitudeCenter,
          lng: decoded.longitudeCenter
        };
      }

      // Decode full Plus Code
      const decoded = olc.decode(cleaned);

      return {
        lat: decoded.latitudeCenter,
        lng: decoded.longitudeCenter
      };
    } catch (error) {
      console.error('Plus Code parsing error:', error);
      return null;
    }
  };

  // Parse coordinates from various formats
  const parseCoordinates = (input: string): { lat: number; lng: number } | null => {
    // Remove any whitespace
    const cleaned = input.trim();

    // Try various coordinate formats:
    // 1. "lat, lng" or "lat,lng"
    // 2. "lat lng"
    // 3. "latitude/longitude"

    // Pattern 1: Comma-separated
    let match = cleaned.match(/^(-?\d+\.?\d*)\s*,\s*(-?\d+\.?\d*)$/);
    if (match) {
      const lat = parseFloat(match[1]);
      const lng = parseFloat(match[2]);
      if (isValidCoordinate(lat, lng)) {
        return { lat, lng };
      }
    }

    // Pattern 2: Space-separated
    match = cleaned.match(/^(-?\d+\.?\d*)\s+(-?\d+\.?\d*)$/);
    if (match) {
      const lat = parseFloat(match[1]);
      const lng = parseFloat(match[2]);
      if (isValidCoordinate(lat, lng)) {
        return { lat, lng };
      }
    }

    // Pattern 3: Slash-separated
    match = cleaned.match(/^(-?\d+\.?\d*)\s*\/\s*(-?\d+\.?\d*)$/);
    if (match) {
      const lat = parseFloat(match[1]);
      const lng = parseFloat(match[2]);
      if (isValidCoordinate(lat, lng)) {
        return { lat, lng };
      }
    }

    return null;
  };

  const isValidCoordinate = (lat: number, lng: number): boolean => {
    return !isNaN(lat) && !isNaN(lng) &&
           lat >= -90 && lat <= 90 &&
           lng >= -180 && lng <= 180;
  };

  // Reverse geocode: Get address from coordinates
  const fetchAddressFromCoords = async (lat: number, lng: number) => {
    setIsLoadingAddress(true);
    setSelectedAddress(null);

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`,
        {
          headers: {
            'User-Agent': 'TelePound Employee Tracker'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Reverse geocoding service unavailable');
      }

      const result = await response.json();

      if (result && result.display_name) {
        // Format address to show most relevant parts
        const address = result.address;
        let formattedAddress = result.display_name;

        // Try to create a shorter, more readable address
        if (address) {
          const parts = [];

          // Building/house number and road
          if (address.road) {
            parts.push(address.road);
            if (address.house_number) {
              parts[0] = `${address.road} ${address.house_number}`;
            }
          }

          // Neighborhood/suburb
          if (address.neighbourhood || address.suburb) {
            parts.push(address.neighbourhood || address.suburb);
          }

          // City
          if (address.city || address.town || address.village) {
            parts.push(address.city || address.town || address.village);
          }

          // State/Province
          if (address.state) {
            parts.push(address.state);
          }

          if (parts.length > 0) {
            formattedAddress = parts.join(', ');
          }
        }

        setSelectedAddress(formattedAddress);
        console.log('📍 Reverse geocoded address:', formattedAddress);
      } else {
        setSelectedAddress('Address not available');
      }
    } catch (error) {
      console.error('📍 Reverse geocoding error:', error);
      setSelectedAddress('Address lookup failed');
    } finally {
      setIsLoadingAddress(false);
    }
  };

  // Search for location using Nominatim (OpenStreetMap geocoding)
  const handleSearch = async () => {
    if (!coordinateInput.trim()) {
      setSearchError('Please enter coordinates or address');
      return;
    }

    setIsSearching(true);
    setSearchError(null);

    try {
      // First, try to parse as Plus Code
      const plusCodeCoords = parsePlusCode(coordinateInput);

      if (plusCodeCoords) {
        // Valid Plus Code - place marker
        console.log('➕ Decoded Plus Code:', plusCodeCoords);
        setSelectedCoords(plusCodeCoords);
        if (mapInstanceRef.current) {
          mapInstanceRef.current.setView([plusCodeCoords.lat, plusCodeCoords.lng], 15);
          addSelectedLocationMarker(mapInstanceRef.current, plusCodeCoords);
        }
        setIsSearching(false);
        return;
      }

      // Second, try to parse as coordinates
      const coords = parseCoordinates(coordinateInput);

      if (coords) {
        // Valid coordinates - place marker
        console.log('📍 Parsed coordinates:', coords);
        setSelectedCoords(coords);
        if (mapInstanceRef.current) {
          mapInstanceRef.current.setView([coords.lat, coords.lng], 15);
          addSelectedLocationMarker(mapInstanceRef.current, coords);
        }
        setIsSearching(false);
        return;
      }

      // If not Plus Code or coordinates, search as address using Nominatim
      console.log('🔍 Searching for address:', coordinateInput);
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(coordinateInput)}&limit=1`,
        {
          headers: {
            'User-Agent': 'TelePound Employee Tracker'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Search service unavailable');
      }

      const results = await response.json();

      if (results && results.length > 0) {
        const result = results[0];
        const lat = parseFloat(result.lat);
        const lng = parseFloat(result.lon);

        console.log('✅ Found location:', result.display_name, { lat, lng });

        const foundCoords = { lat, lng };
        setSelectedCoords(foundCoords);

        if (mapInstanceRef.current) {
          mapInstanceRef.current.setView([lat, lng], 15);
          addSelectedLocationMarker(mapInstanceRef.current, foundCoords);
        }
      } else {
        setSearchError('Location not found. Try coordinates like: -6.200000, 106.816666');
      }
    } catch (error) {
      console.error('🔍 Search error:', error);
      setSearchError('Search failed. Please try coordinates instead.');
    } finally {
      setIsSearching(false);
    }
  };

  // Handle Enter key press in input
  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Handle confirm selection
  const handleConfirm = () => {
    if (selectedCoords) {
      onLocationSelect(selectedCoords);
      onClose();
    }
  };

  // Handle location when modal opens and auto-select it
  useEffect(() => {
    if (!isOpen || !isMapReady) return;

    // Only use initialLocation if provided (for editing existing customers)
    if (initialLocation) {
      console.log('📍 Using initialLocation from props (editing mode), skipping location auto-selection');
      return;
    }

    // If location has already been selected or set, skip
    if (selectedCoords || userLocation) {
      console.log('📍 Location already selected or GPS already requested, skipping');
      return;
    }

    // Use currentUserLocation if provided from parent component
    if (currentUserLocation) {
      console.log('📍 Using currentUserLocation from props:', currentUserLocation);
      const providedLocation = {
        lat: currentUserLocation.lat,
        lng: currentUserLocation.lng,
        accuracy: undefined // Parent component doesn't provide accuracy
      };

      // Set as user location
      setUserLocation(providedLocation);

      // Auto-select this location
      setSelectedCoords({ lat: providedLocation.lat, lng: providedLocation.lng });

      // Add markers to map
      if (mapInstanceRef.current) {
        addCurrentLocationMarker(mapInstanceRef.current, providedLocation);
        addSelectedLocationMarker(mapInstanceRef.current, { lat: providedLocation.lat, lng: providedLocation.lng });

        // Center map on provided location with appropriate zoom
        const zoom = getContextZoom(providedLocation, false); // Not GPS, so use context zoom
        mapInstanceRef.current.setView([providedLocation.lat, providedLocation.lng], zoom);
      }
      return;
    }

    console.log('📍 No location provided, requesting GPS location for auto-selection...');

    // Request current position with high accuracy
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const detectedLocation = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            accuracy: position.coords.accuracy
          };

          console.log('✅ GPS location detected:', detectedLocation);

          // Set as user location
          setUserLocation(detectedLocation);

          // Auto-select this location
          setSelectedCoords({ lat: detectedLocation.lat, lng: detectedLocation.lng });

          // Add markers to map
          if (mapInstanceRef.current) {
            addCurrentLocationMarker(mapInstanceRef.current, detectedLocation);
            addSelectedLocationMarker(mapInstanceRef.current, { lat: detectedLocation.lat, lng: detectedLocation.lng });

            // Center map on detected location
            const zoom = getAccuracyBasedZoom(detectedLocation.accuracy);
            mapInstanceRef.current.setView([detectedLocation.lat, detectedLocation.lng], zoom);
          }
        },
        (error) => {
          console.warn('📍 GPS location request failed:', error.message);
          // Don't show error to user, they can still select manually
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        }
      );
    }
  }, [isOpen, isMapReady, selectedCoords, userLocation, initialLocation, currentUserLocation]);

  // Fetch address when coordinates are selected
  useEffect(() => {
    if (selectedCoords) {
      fetchAddressFromCoords(selectedCoords.lat, selectedCoords.lng);
    } else {
      setSelectedAddress(null);
    }
  }, [selectedCoords]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 backdrop-blur-sm flex items-center justify-center z-[70] p-4">
      <div className="bg-card rounded-xl w-full max-w-4xl h-[80vh] flex flex-col border border-border shadow-lg">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border bg-card">
          <h2 className="text-lg font-semibold text-foreground">{t('location_picker.title')}</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted/50 rounded-full transition-colors text-muted-foreground hover:text-foreground"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Search Box */}
        <div className="p-4 border-b border-border">
          <div className="flex flex-col space-y-2">
            <div className="flex space-x-2">
              <div className="flex-1 relative">
                <input
                  type="text"
                  value={coordinateInput}
                  onChange={(e) => setCoordinateInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={t('location_picker.search_placeholder')}
                  className="w-full px-4 py-2 pl-10 border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  disabled={isSearching}
                />
                <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
              </div>
              <button
                onClick={handleSearch}
                disabled={isSearching || !coordinateInput.trim()}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
              >
                {isSearching ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-foreground mr-2"></div>
                    {t('location_picker.searching')}
                  </>
                ) : (
                  <>
                    <Search className="w-4 h-4 mr-2" />
                    {t('location_picker.search_button')}
                  </>
                )}
              </button>
            </div>
            {searchError && (
              <div className="text-xs text-red-600 dark:text-red-400 flex items-center">
                <X className="w-3 h-3 mr-1" />
                {searchError}
              </div>
            )}
            {/* <div className="flex items-center text-xs text-muted-foreground">
              <Crosshair className="w-3 h-3 mr-1" />
              <span>{t('location_picker.click_map_hint')}</span>
            </div> */}

            {/* Help Button */}
            <button
              onClick={() => setShowHelp(!showHelp)}
              className="flex items-center text-xs text-primary dark:text-primary hover:text-primary/80 dark:hover:text-primary/80 transition-colors"
            >
              <HelpCircle className="w-3 h-3 mr-1" />
              <span>Need help? View examples</span>
              {showHelp ? <ChevronUp className="w-3 h-3 ml-1" /> : <ChevronDown className="w-3 h-3 ml-1" />}
            </button>

            {/* Collapsible Help Content */}
            {showHelp && (
              <div className="mt-2 p-3 bg-primary/10 dark:bg-primary/20 border border-primary/20 dark:border-primary/30 rounded-lg text-xs space-y-2">
                <div className="font-semibold text-primary dark:text-primary">You can search using:</div>

                <div>
                  <div className="font-medium text-primary dark:text-primary">📍 Full Address:</div>
                  <div className="text-primary/80 dark:text-primary/80 font-mono text-xs mt-1">
                    Komplek Duta Indah Karya, Daan Mogot Rd No.KM. 13 Blok B.16, Rawa Buaya, Cengkareng, West Jakarta City, Jakarta 11740
                  </div>
                </div>

                <div>
                  <div className="font-medium text-primary dark:text-primary">🗺️ Coordinates (from Google Maps):</div>
                  <div className="text-primary/80 dark:text-primary/80 font-mono text-xs mt-1">
                    -6.159099,106.7333483
                  </div>
                </div>

                <div>
                  <div className="font-medium text-primary dark:text-primary">➕ Plus Code:</div>
                  <div className="text-primary/80 dark:text-primary/80 font-mono text-xs mt-1">
                    RPRM+8W Rawa Buaya, West Jakarta City, Jakarta
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Map Container */}
        <div className="flex-1 relative">
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-3"></div>
                <p className="text-muted-foreground">{t('location_picker.loading_map')}</p>
              </div>
            </div>
          ) : mapError ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <MapPin className="w-12 h-12 text-red-400 mx-auto mb-3" />
                <p className="text-red-600">{mapError}</p>
              </div>
            </div>
          ) : (
            <>
              {/* Map */}
              <div
                ref={mapRef}
                className="h-full w-full"
                style={{ minHeight: '400px' }}
              />


              {/* Selected Location Info - Fixed z-index to appear above map */}
              {selectedCoords && (
                <div className="absolute bottom-4 left-4 bg-card/95 backdrop-blur-sm rounded-lg px-4 py-3 shadow-xl z-[1000] border border-border max-w-sm">
                  <div className="flex items-center text-sm font-medium text-foreground mb-1">
                    <MapPin className="w-4 h-4 mr-2 text-red-500" />
                    {t('location_picker.selected_location')}
                  </div>
                  <div className="text-xs text-muted-foreground font-mono mb-2">
                    {selectedCoords.lat.toFixed(6)}, {selectedCoords.lng.toFixed(6)}
                  </div>
                  {isLoadingAddress ? (
                    <div className="flex items-center text-xs text-muted-foreground">
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-primary mr-2"></div>
                      <span>Loading address...</span>
                    </div>
                  ) : selectedAddress && (
                    <div className="text-xs text-foreground leading-relaxed border-t border-border pt-2">
                      {selectedAddress}
                    </div>
                  )}
                </div>
              )}

            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-border bg-muted/20 flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            {selectedCoords ? (
              <span className="text-green-600 dark:text-green-400">{t('location_picker.location_selected')}</span>
            ) : (
              <span>{t('location_picker.click_to_select')}</span>
            )}
          </div>
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-border text-muted-foreground rounded-lg hover:bg-muted/50 transition-colors bg-background"
            >
              {t('location_picker.cancel')}
            </button>
            <button
              onClick={handleConfirm}
              disabled={!selectedCoords}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
            >
              <Check className="w-4 h-4 mr-2" />
              {t('location_picker.confirm')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};